import { useCallback, useEffect, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Button, Checkbox, NumberInput, Select, Table, TextInput, Textarea } from '@mantine/core'
import { useDebouncedValue } from '@mantine/hooks'
import {
  createDevice,
  fetchDevice,
  fetchDeviceCommands,
  fetchDeviceEvents,
  fetchDevicePayments,
  fetchDevices,
  fetchSites,
  revokeDevice,
  sendDeviceCommand,
  updateDevice,
} from '../../admin/server-fns'
import { mergeActivity } from '../../admin/activity'
import type { ActivityRow } from '../../admin/activity'
import type {
  SerializedEventsResponse,
  SerializedPaymentsResponse,
} from '../../admin/server-fns'
import type { CreateResponse, DeviceView } from '../../api/generated/schemas'
import { DeviceTelemetry } from '../../admin/device-telemetry'
import { DeviceActivityFeed } from '../../admin/device-activity'
import { DeviceBoards } from '../../admin/device-boards'
import { DeviceBoxes } from '../../admin/device-boxes'
import { DevicePickupCodes } from '../../admin/pickup-codes'
import {
  AdminRouteError,
  Modal,
  PageHeader,
  Pagination,
  Panel,
  Spinner,
  StatusBadge,
  formatDate,
  kickerCls,
  tdCls,
  thCls,
  totalPages,
} from '../../admin/ui'

const STATUSES = ['active', 'provisioning', 'suspended', 'revoked', 'offline']
const PAGE_LIMIT = 20

/* The commands the agent actually implements (proto::CommandPayload), with the
   fields each variant requires. The API validates the same set and refuses
   anything else before writing a row, so this list and
   storage-api/src/command.rs must move together. */
type Field = {
  name: string
  label: string
  kind: 'number' | 'text' | 'bool' | 'json'
  required?: boolean
  initial?: string
  help?: string
}

type CommandDef = {
  value: string
  label: string
  description: string
  fields: Field[]
  build: (v: Record<string, string>) => Record<string, unknown>
}

const UPDATE_SPEC: Field[] = [
  { name: 'url', label: 'Artifact URL', kind: 'text', required: true },
  { name: 'sha256', label: 'SHA-256', kind: 'text', required: true, help: 'Hex digest of the artifact bytes.' },
  { name: 'sig', label: 'Signature', kind: 'text', required: true, help: 'Base64 ed25519 signature.' },
]

const spec = (v: Record<string, string>) => ({ url: v.url, sha256: v.sha256, sig: v.sig })

const COMMANDS: CommandDef[] = [
  {
    value: 'reboot',
    label: 'Reboot',
    description: 'Restart the unit after an optional delay.',
    fields: [
      {
        name: 'delay_s',
        label: 'Delay (seconds)',
        kind: 'number',
        initial: '0',
        help: '0 reboots as soon as the device acknowledges the command.',
      },
    ],
    build: (v) => ({ delay_s: Number(v.delay_s || 0) }),
  },
  {
    value: 'open_box',
    label: 'Open box',
    description: 'Pulse a single latch open.',
    fields: [{ name: 'box_id', label: 'Box channel', kind: 'number', required: true, help: '0–255.' }],
    build: (v) => ({ box_id: Number(v.box_id) }),
  },
  {
    value: 'get_logs',
    label: 'Get logs',
    description: 'Ask the unit to upload its recent journal.',
    fields: [
      { name: 'since_s', label: 'Since (seconds)', kind: 'number', required: true, initial: '900' },
    ],
    build: (v) => ({ since_s: Number(v.since_s) }),
  },
  {
    value: 'config_set',
    label: 'Set config key',
    description: 'Push a single server-side config override.',
    fields: [
      { name: 'key', label: 'Key', kind: 'text', required: true },
      {
        name: 'value',
        label: 'Value (JSON)',
        kind: 'json',
        required: true,
        help: 'Any JSON value — a string must be quoted.',
      },
    ],
    build: (v) => ({ key: v.key, value: JSON.parse(v.value) as unknown }),
  },
  {
    value: 'app_update',
    label: 'Update agent',
    description: 'Replace the storage-agent binary.',
    fields: UPDATE_SPEC,
    build: spec,
  },
  {
    value: 'kiosk_update',
    label: 'Update kiosk',
    description: 'Replace the kiosk binary.',
    fields: UPDATE_SPEC,
    build: spec,
  },
  {
    value: 'os_update',
    label: 'Update OS',
    description: 'Install an OS image via Mender.',
    fields: [
      ...UPDATE_SPEC,
      {
        name: 'immediate',
        label: 'Reboot immediately',
        kind: 'bool',
        help: 'Off waits for the next maintenance window.',
      },
    ],
    build: (v) => ({ spec: spec(v), immediate: v.immediate === 'true' }),
  },
  {
    value: 'codes_sync',
    label: 'Sync pickup codes',
    description: 'Replace the offline-usable signed code set.',
    fields: [
      {
        name: 'codes',
        label: 'Codes (JSON array)',
        kind: 'json',
        required: true,
        initial: '[]',
        help: 'Objects of code_hash, box_id, expires (epoch millis) and sig.',
      },
    ],
    build: (v) => ({ codes: JSON.parse(v.codes) as unknown }),
  },
]

type DeviceSearch = {
  status?: string
  siteId?: string
  fleetId?: string
}

export const Route = createFileRoute('/admin/devices')({
  validateSearch: (search: Record<string, unknown>): DeviceSearch => ({
    status: typeof search.status === 'string' && search.status ? search.status : undefined,
    siteId: typeof search.siteId === 'string' && search.siteId ? search.siteId : undefined,
    fleetId: typeof search.fleetId === 'string' && search.fleetId ? search.fleetId : undefined,
  }),
  loader: async () => fetchDevices({ data: { page: 1, limit: PAGE_LIMIT } }),
  component: Devices,
  errorComponent: AdminRouteError,
})

function Devices() {
  const initialData = Route.useLoaderData()
  const urlFilters = Route.useSearch()
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const [devices, setDevices] = useState<DeviceView[]>(initialData.items)
  const [total, setTotal] = useState(initialData.total)
  const [pages, setPages] = useState(totalPages(initialData.total, initialData.limit))
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [debouncedSearch] = useDebouncedValue(search, 300)
  const [statusFilter, setStatusFilter] = useState<string | null>(urlFilters.status ?? null)
  const [siteFilter, setSiteFilter] = useState(urlFilters.siteId ?? '')
  const [fleetFilter, setFleetFilter] = useState(urlFilters.fleetId ?? '')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await fetchDevices({
        data: {
          page,
          limit: PAGE_LIMIT,
          ...(debouncedSearch ? { search: debouncedSearch } : {}),
          ...(statusFilter ? { status: statusFilter } : {}),
          ...(siteFilter ? { siteId: siteFilter } : {}),
          ...(fleetFilter ? { fleetId: fleetFilter } : {}),
        },
      })
      setDevices(data.items)
      setTotal(data.total)
      setPages(totalPages(data.total, data.limit))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load devices.')
    } finally {
      setLoading(false)
    }
  }, [page, debouncedSearch, statusFilter, siteFilter, fleetFilter])

  useEffect(() => {
    if (page !== 1 || debouncedSearch || statusFilter || siteFilter || fleetFilter) {
      void refresh()
    }
  }, [refresh, page, debouncedSearch, statusFilter, siteFilter, fleetFilter])

  const [addOpen, setAddOpen] = useState(false)

  if (selectedId) {
    return (
      <DeviceDetail
        deviceId={selectedId}
        onBack={() => {
          setSelectedId(null)
          void refresh()
        }}
      />
    )
  }

  return (
    <>
      <PageHeader
        kicker="Fleet"
        title="Devices"
        actions={<Button onClick={() => setAddOpen(true)}>Add device</Button>}
      />

      <div className="mb-2 flex flex-wrap items-center gap-2">
        <TextInput
          type="search"
          placeholder="Search serial, name…"
          className="w-full max-w-xs"
          value={search}
          onChange={(e) => {
            setSearch(e.currentTarget.value)
            setPage(1)
          }}
        />
        <Select
          placeholder="All statuses"
          data={STATUSES}
          value={statusFilter}
          onChange={(value) => {
            setStatusFilter(value)
            setPage(1)
          }}
          clearable
        />
        {siteFilter ? (
          <Button
            variant="light"
            size="compact-sm"
            onClick={() => {
              setSiteFilter('')
              setPage(1)
            }}
          >
            Site: {siteFilter} ✕
          </Button>
        ) : null}
        {fleetFilter ? (
          <Button
            variant="light"
            size="compact-sm"
            onClick={() => {
              setFleetFilter('')
              setPage(1)
            }}
          >
            Fleet: {fleetFilter} ✕
          </Button>
        ) : null}
      </div>

      <p className="mb-3 mt-0 font-mono text-xs text-slate-400 dark:text-slate-500">
        {total} device{total === 1 ? '' : 's'}
      </p>

      {error ? (
        <Panel className="mb-4 border-red-300 px-4 py-3 text-sm text-red-700 dark:border-red-500/40 dark:text-red-400">
          {error}{' '}
          <Button variant="subtle" size="compact-sm" onClick={() => void refresh()}>
            Retry
          </Button>
        </Panel>
      ) : null}

      <Panel>
        {loading ? (
          <Spinner />
        ) : devices.length === 0 ? (
          <p className="m-0 px-4 py-12 text-center text-sm text-slate-500 dark:text-slate-400">
            No devices match. Clear the search or filters, or add a device.
          </p>
        ) : (
          <Table.ScrollContainer minWidth={1040}>
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th className={thCls}>Serial</Table.Th>
                  <Table.Th className={thCls}>Name</Table.Th>
                  <Table.Th className={thCls}>Status</Table.Th>
                  <Table.Th className={thCls}>Link</Table.Th>
                  <Table.Th className={thCls}>SoC temp</Table.Th>
                  <Table.Th className={thCls}>Site</Table.Th>
                  <Table.Th className={thCls}>Fleet</Table.Th>
                  <Table.Th className={thCls}>Firmware</Table.Th>
                  <Table.Th className={thCls}>Last seen</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {devices.map((d) => (
                  <Table.Tr
                    key={d.id}
                    className="cursor-pointer"
                    onClick={() => setSelectedId(d.id)}
                  >
                    <Table.Td className={`${tdCls} font-mono text-xs`}>{d.serialNumber}</Table.Td>
                    <Table.Td className={tdCls}>{d.name}</Table.Td>
                    <Table.Td className={tdCls}>
                      <StatusBadge status={d.status} />
                    </Table.Td>
                    <Table.Td className={tdCls}>
                      <LinkBadge device={d} />
                    </Table.Td>
                    <Table.Td className={`${tdCls} font-mono text-xs [font-variant-numeric:tabular-nums]`}>
                      {d.lastSocTempMc != null ? `${(d.lastSocTempMc / 1000).toFixed(1)} °C` : '—'}
                    </Table.Td>
                    <Table.Td className={`${tdCls} font-mono text-xs`}>{d.siteId ?? '—'}</Table.Td>
                    <Table.Td className={`${tdCls} font-mono text-xs`}>
                      {d.fleetId ?? '—'}
                    </Table.Td>
                    <Table.Td className={`${tdCls} font-mono text-xs`}>
                      {d.firmwareVersion ?? '—'}
                    </Table.Td>
                    <Table.Td className={`${tdCls} font-mono text-xs`}>
                      {formatDate(d.lastSeenAt)}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Panel>

      <div className="mt-4">
        <Pagination page={page} pages={pages} onChange={setPage} />
      </div>

      <AddDeviceModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={() => void refresh()}
      />
    </>
  )
}

/* Live link state, denormalized onto the device row by telemetry ingest. A
   device that has never reported is unknown, not offline — the broker's
   last-will is what flips a device that had been reporting to offline. */
function LinkBadge({ device }: { device: DeviceView }) {
  if (!device.lastTelemetryAt)
    return <span className="font-mono text-xs text-slate-400 dark:text-slate-500">—</span>

  return (
    <span
      className="inline-flex items-center gap-1.5 font-mono text-xs text-slate-600 dark:text-slate-300"
      title={`Last telemetry ${formatDate(device.lastTelemetryAt)}`}
    >
      <span
        className={`h-1.5 w-1.5 rounded-full ${device.online ? 'bg-emerald-500' : 'bg-red-500'}`}
      />
      {device.online ? 'online' : 'offline'}
    </span>
  )
}

/* Searchable site picker backed by the sites API. Keeps an unknown current
   value selectable by falling back to the raw ID as its label. */
function SiteSelect({
  label,
  value,
  onChange,
}: {
  label: string
  value: string | null
  onChange: (value: string | null) => void
}) {
  const [search, setSearch] = useState('')
  const [debouncedSearch] = useDebouncedValue(search, 300)
  const [options, setOptions] = useState<Array<{ value: string; label: string }>>([])

  useEffect(() => {
    fetchSites({
      data: { page: 1, limit: 20, ...(debouncedSearch ? { search: debouncedSearch } : {}) },
    })
      .then((data) =>
        setOptions(
          data.items.map((s) => ({
            value: s.id,
            label: s.code ? `${s.name} · ${s.code}` : s.name,
          })),
        ),
      )
      .catch(() => setOptions([]))
  }, [debouncedSearch])

  const data =
    value && !options.some((o) => o.value === value)
      ? [...options, { value, label: value }]
      : options

  return (
    <Select
      label={label}
      placeholder="Search sites…"
      data={data}
      value={value}
      onChange={onChange}
      searchable
      searchValue={search}
      onSearchChange={setSearch}
      filter={({ options: opts }) => opts}
      clearable
      nothingFoundMessage="No sites match."
    />
  )
}

function AddDeviceModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean
  onClose: () => void
  onCreated: () => void
}) {
  const [serial, setSerial] = useState('')
  const [name, setName] = useState('')
  const [siteId, setSiteId] = useState<string | null>(null)
  const [fleetId, setFleetId] = useState('')
  const [notes, setNotes] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [created, setCreated] = useState<CreateResponse | null>(null)
  const [copied, setCopied] = useState(false)

  const reset = () => {
    setSerial('')
    setName('')
    setSiteId(null)
    setFleetId('')
    setNotes('')
    setError(null)
    setCreated(null)
    setCopied(false)
  }

  const close = () => {
    reset()
    onClose()
  }

  const handleCreate = async () => {
    setBusy(true)
    setError(null)
    try {
      const res = await createDevice({
        data: {
          serialNumber: serial,
          name: name || null,
          siteId: siteId || null,
          fleetId: fleetId || null,
          notes: notes || null,
        },
      })
      setCreated(res)
      onCreated()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not create the device.')
    } finally {
      setBusy(false)
    }
  }

  const copyToken = () => {
    if (!created) return
    void navigator.clipboard.writeText(created.bootstrapToken).then(() => setCopied(true))
  }

  if (created) {
    return (
      <Modal open={open} onClose={close} title="Device created">
        <p className="m-0 text-sm text-slate-600 dark:text-slate-400">
          <span className="font-mono text-xs">{created.device.serialNumber}</span> is registered.
          Enter this bootstrap token on the device to provision it.
        </p>
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 dark:border-amber-500/40 dark:bg-amber-500/10">
          <p className="m-0 mb-2 break-all font-mono text-xs text-slate-900 dark:text-slate-100">
            {created.bootstrapToken}
          </p>
          <p className={`${kickerCls} m-0`}>
            Shown once · expires {formatDate(created.bootstrapTokenExpiresAt)}
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={copyToken}>{copied ? 'Copied' : 'Copy token'}</Button>
          <Button variant="default" onClick={close}>
            Done
          </Button>
        </div>
      </Modal>
    )
  }

  return (
    <Modal open={open} onClose={close} title="Add device">
      <TextInput
        label="Serial number"
        required
        data-autofocus
        value={serial}
        onChange={(e) => setSerial(e.currentTarget.value)}
      />
      <TextInput
        label="Name (optional)"
        value={name}
        onChange={(e) => setName(e.currentTarget.value)}
      />
      <div className="grid grid-cols-2 gap-3">
        <SiteSelect label="Site (optional)" value={siteId} onChange={setSiteId} />
        <TextInput
          label="Fleet ID (optional)"
          value={fleetId}
          onChange={(e) => setFleetId(e.currentTarget.value)}
        />
      </div>
      <Textarea
        label="Notes (optional)"
        rows={3}
        value={notes}
        onChange={(e) => setNotes(e.currentTarget.value)}
      />
      {error ? <p className="m-0 text-sm text-red-600 dark:text-red-400">{error}</p> : null}
      <Button disabled={!serial.trim()} loading={busy} onClick={() => void handleCreate()}>
        Create device
      </Button>
    </Modal>
  )
}

function DeviceDetail({ deviceId, onBack }: { deviceId: string; onBack: () => void }) {
  const [device, setDevice] = useState<DeviceView | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setError(null)
    try {
      setDevice(await fetchDevice({ data: { deviceId } }))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the device.')
    }
  }, [deviceId])

  useEffect(() => {
    void load()
  }, [load])

  const [editOpen, setEditOpen] = useState(false)
  const [cmdOpen, setCmdOpen] = useState(false)
  const [revokeOpen, setRevokeOpen] = useState(false)

  if (error) {
    return (
      <>
        <Button variant="default" className="mb-4" onClick={onBack}>
          ← Back to devices
        </Button>
        <Panel className="border-red-300 px-4 py-3 text-sm text-red-700 dark:border-red-500/40 dark:text-red-400">
          {error}
        </Panel>
      </>
    )
  }

  if (!device) return <Spinner />

  const meta: Array<[string, string]> = [
    ['Device ID', device.id],
    ['Name', device.name],
    ['Site', device.siteId ?? '—'],
    ['Fleet', device.fleetId ?? '—'],
    ['Firmware', device.firmwareVersion ?? '—'],
    ['Config version', String(device.configVersion)],
    ['Last seen', formatDate(device.lastSeenAt)],
    ['Created', formatDate(device.createdAt)],
    ['Updated', formatDate(device.updatedAt)],
  ]
  if (device.revokedAt) {
    meta.push(
      ['Revoked', formatDate(device.revokedAt)],
      ['Revoked by', device.revokedBy ?? '—'],
      ['Revoke reason', device.revokedReason ?? '—'],
    )
  }

  return (
    <>
      <Button variant="default" className="mb-4" onClick={onBack}>
        ← Back to devices
      </Button>

      <PageHeader
        kicker="Device"
        title={device.serialNumber}
        actions={
          <>
            <Button variant="default" onClick={() => setEditOpen(true)}>
              Edit
            </Button>
            <Button onClick={() => setCmdOpen(true)}>Send command</Button>
            <Button color="red" variant="outline" onClick={() => setRevokeOpen(true)}>
              Revoke
            </Button>
          </>
        }
      />

      <div className="mb-3">
        <StatusBadge status={device.status} />
      </div>

      <Panel className="mb-6 p-5">
        <dl className="m-0 grid gap-x-8 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
          {meta.map(([key, value]) => (
            <div key={key}>
              <dt className={`${kickerCls} mb-0.5`}>{key}</dt>
              <dd className="m-0 break-all font-mono text-xs text-slate-800 dark:text-slate-200">
                {value}
              </dd>
            </div>
          ))}
          {device.notes ? (
            <div className="sm:col-span-2 lg:col-span-3">
              <dt className={`${kickerCls} mb-0.5`}>Notes</dt>
              <dd className="m-0 text-sm text-slate-700 dark:text-slate-300">{device.notes}</dd>
            </div>
          ) : null}
        </dl>
      </Panel>

      {/* Boards first: box IDs are counted across them, so the layout is the
          thing the box matrix below is expressed in terms of. */}
      <DeviceBoards deviceId={device.id} />

      <DeviceBoxes deviceId={device.id} />

      {/* After the box matrix: a code opens one of those boxes, so the table
          has to exist before issuing makes sense. */}
      <DevicePickupCodes deviceId={device.id} />

      <DeviceActivity deviceId={device.id} />

      <EditDeviceModal
        open={editOpen}
        device={device}
        onClose={() => setEditOpen(false)}
        onSaved={(updated) => {
          setDevice(updated)
          setEditOpen(false)
        }}
      />
      <CommandModal open={cmdOpen} deviceId={device.id} onClose={() => setCmdOpen(false)} />
      <RevokeModal
        open={revokeOpen}
        deviceId={device.id}
        serial={device.serialNumber}
        onClose={() => setRevokeOpen(false)}
        onRevoked={(updated) => {
          setDevice(updated)
          setRevokeOpen(false)
        }}
      />
    </>
  )
}

function DeviceActivity({ deviceId }: { deviceId: string }) {
  const [tab, setTab] = useState<'telemetry' | 'events' | 'payments' | 'activity'>('telemetry')

  return (
    <Panel>
      <div className="flex gap-1 border-b border-slate-200 px-3 pt-2 dark:border-slate-800">
        {(['telemetry', 'events', 'payments', 'activity'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`rounded-t-lg border-b-2 px-3 py-2 text-sm font-semibold capitalize transition ${
              tab === t
                ? 'border-blue-700 text-blue-700 dark:border-blue-400 dark:text-blue-400'
                : 'border-transparent text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white'
            }`}
          >
            {t}
          </button>
        ))}
      </div>
      {tab === 'telemetry' ? (
        <DeviceTelemetry deviceId={deviceId} />
      ) : tab === 'events' ? (
        <DeviceEvents deviceId={deviceId} />
      ) : tab === 'payments' ? (
        <DevicePayments deviceId={deviceId} />
      ) : (
        <DeviceActivityFeed deviceId={deviceId} />
      )}
    </Panel>
  )
}

function DeviceEvents({ deviceId }: { deviceId: string }) {
  const [data, setData] = useState<SerializedEventsResponse | null>(null)
  const [page, setPage] = useState(1)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setError(null)
    fetchDeviceEvents({ data: { deviceId, params: { page, limit: 10 } } })
      .then(setData)
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : 'Could not load events.'),
      )
  }, [deviceId, page])

  if (error)
    return <p className="m-0 px-4 py-8 text-sm text-red-600 dark:text-red-400">{error}</p>
  if (!data) return <Spinner />
  if (data.items.length === 0)
    return (
      <p className="m-0 px-4 py-10 text-center text-sm text-slate-500 dark:text-slate-400">
        No events reported by this device yet.
      </p>
    )

  return (
    <>
      <Table.ScrollContainer minWidth={640}>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th className={thCls}>Type</Table.Th>
              <Table.Th className={thCls}>Received</Table.Th>
              <Table.Th className={thCls}>Payload</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {data.items.map((ev) => (
              <Table.Tr key={ev.id}>
                <Table.Td className={`${tdCls} font-mono text-xs`}>{ev.type}</Table.Td>
                <Table.Td className={`${tdCls} font-mono text-xs`}>
                  {formatDate(ev.receivedAt)}
                </Table.Td>
                <Table.Td className={`${tdCls} max-w-md truncate font-mono text-xs`}>
                  {ev.payload}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      <div className="px-4 py-3">
        <Pagination page={page} pages={totalPages(data.total, data.limit)} onChange={setPage} />
      </div>
    </>
  )
}

function DevicePayments({ deviceId }: { deviceId: string }) {
  const [data, setData] = useState<SerializedPaymentsResponse | null>(null)
  const [page, setPage] = useState(1)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setError(null)
    fetchDevicePayments({ data: { deviceId, params: { page, limit: 10 } } })
      .then(setData)
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : 'Could not load payments.'),
      )
  }, [deviceId, page])

  if (error)
    return <p className="m-0 px-4 py-8 text-sm text-red-600 dark:text-red-400">{error}</p>
  if (!data) return <Spinner />
  if (data.items.length === 0)
    return (
      <p className="m-0 px-4 py-10 text-center text-sm text-slate-500 dark:text-slate-400">
        No payments recorded on this device yet.
      </p>
    )

  return (
    <>
      <Table.ScrollContainer minWidth={640}>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th className={thCls}>Payment</Table.Th>
              <Table.Th className={thCls}>Amount</Table.Th>
              <Table.Th className={thCls}>Status</Table.Th>
              <Table.Th className={thCls}>Paid</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {data.items.map((p) => (
              <Table.Tr key={p.id}>
                <Table.Td className={`${tdCls} font-mono text-xs`}>{p.id}</Table.Td>
                <Table.Td className={`${tdCls} font-mono text-xs`}>
                  {p.amount} {p.currency}
                </Table.Td>
                <Table.Td className={tdCls}>
                  <StatusBadge status={p.status} />
                </Table.Td>
                <Table.Td className={`${tdCls} font-mono text-xs`}>
                  {formatDate(p.paidAt)}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      <div className="px-4 py-3">
        <Pagination page={page} pages={totalPages(data.total, data.limit)} onChange={setPage} />
      </div>
    </>
  )
}

function EditDeviceModal({
  open,
  device,
  onClose,
  onSaved,
}: {
  open: boolean
  device: DeviceView
  onClose: () => void
  onSaved: (device: DeviceView) => void
}) {
  const [name, setName] = useState(device.name)
  const [siteId, setSiteId] = useState<string | null>(device.siteId ?? null)
  const [fleetId, setFleetId] = useState(device.fleetId ?? '')
  const [notes, setNotes] = useState(device.notes ?? '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setName(device.name)
      setSiteId(device.siteId ?? null)
      setFleetId(device.fleetId ?? '')
      setNotes(device.notes ?? '')
      setError(null)
    }
  }, [open, device])

  const handleSave = async () => {
    setBusy(true)
    setError(null)
    try {
      const updated = await updateDevice({
        data: {
          deviceId: device.id,
          body: {
            name: name || null,
            siteId: siteId || null,
            fleetId: fleetId || null,
            notes: notes || null,
          },
        },
      })
      onSaved(updated)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save changes.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Edit device">
      <TextInput
        label="Name"
        data-autofocus
        value={name}
        onChange={(e) => setName(e.currentTarget.value)}
      />
      <div className="grid grid-cols-2 gap-3">
        <SiteSelect label="Site" value={siteId} onChange={setSiteId} />
        <TextInput
          label="Fleet ID"
          value={fleetId}
          onChange={(e) => setFleetId(e.currentTarget.value)}
        />
      </div>
      <Textarea
        label="Notes"
        rows={3}
        value={notes}
        onChange={(e) => setNotes(e.currentTarget.value)}
      />
      {error ? <p className="m-0 text-sm text-red-600 dark:text-red-400">{error}</p> : null}
      <Button loading={busy} onClick={() => void handleSave()}>
        Save changes
      </Button>
    </Modal>
  )
}

/**
 * The last 10 commands/events for this device, polled every 3s while
 * mounted. Rendered only while the modal is open (see `CommandModal`) so
 * the interval starts on open and is cleared by the unmount on close —
 * the just-sent command starts `pending` and this is what shows its
 * `cmd_ack` landing without the operator hunting for the Activity tab.
 */
function RecentActivity({ deviceId, refreshKey }: { deviceId: string; refreshKey: number }) {
  const [rows, setRows] = useState<ActivityRow[] | null>(null)

  const load = useCallback(async () => {
    try {
      const [commands, events] = await Promise.all([
        fetchDeviceCommands({ data: { deviceId, params: { page: 1, limit: 10 } } }),
        fetchDeviceEvents({ data: { deviceId, params: { page: 1, limit: 10 } } }),
      ])
      setRows(mergeActivity(commands.items, events.items).slice(0, 10))
    } catch {
      // Best-effort inline list; the full Activity tab is the source of
      // truth if this quietly fails to load.
    }
  }, [deviceId])

  useEffect(() => {
    void load()
  }, [load, refreshKey])

  useEffect(() => {
    const id = setInterval(() => void load(), 3000)
    return () => clearInterval(id)
  }, [load])

  if (!rows || rows.length === 0) return null

  return (
    <div className="mt-3 border-t border-slate-200 pt-3 dark:border-slate-800">
      <p className={`${kickerCls} mb-2`}>Recent activity</p>
      <ul className="m-0 flex list-none flex-col gap-1 p-0">
        {rows.map((r) => (
          <li key={`${r.kind}-${r.id}`} className="flex items-center gap-2 font-mono text-xs">
            <span className="text-slate-400">{formatDate(r.at)}</span>
            <span>{r.type}</span>
            {r.status ? <StatusBadge status={r.status} /> : null}
          </li>
        ))}
      </ul>
    </div>
  )
}

function CommandModal({
  open,
  deviceId,
  onClose,
}: {
  open: boolean
  deviceId: string
  onClose: () => void
}) {
  const [type, setType] = useState<string | null>(null)
  const [values, setValues] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sent, setSent] = useState<string | null>(null)
  const [refreshKey, setRefreshKey] = useState(0)

  const command = COMMANDS.find((c) => c.value === type) ?? null

  const selectCommand = (next: string | null) => {
    setType(next)
    setError(null)
    const def = COMMANDS.find((c) => c.value === next)
    setValues(
      def ? Object.fromEntries(def.fields.map((f) => [f.name, f.initial ?? ''])) : {},
    )
  }

  const close = () => {
    selectCommand(null)
    setSent(null)
    onClose()
  }

  const set = (name: string, value: string) => setValues((v) => ({ ...v, [name]: value }))

  const incomplete =
    !command || command.fields.some((f) => f.required && !values[f.name]?.trim())

  const handleSend = async () => {
    if (!command) return
    setBusy(true)
    setError(null)
    try {
      let payload: Record<string, unknown>
      try {
        payload = command.build(values)
      } catch {
        setError('A JSON field is not valid JSON.')
        setBusy(false)
        return
      }
      const res = await sendDeviceCommand({
        data: { deviceId, body: { type: command.value, payload } },
      })
      setSent(`${res.type} queued · ${res.status}`)
      setRefreshKey((k) => k + 1)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not send the command.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal open={open} onClose={close} title="Send command">
      {sent ? (
        <>
          <p className="m-0 font-mono text-xs text-emerald-600 dark:text-emerald-400">{sent}</p>
          <Button variant="default" onClick={close}>
            Done
          </Button>
        </>
      ) : (
        <>
          <Select
            label="Command"
            placeholder="Choose a command…"
            data={COMMANDS.map((c) => ({ value: c.value, label: c.label }))}
            value={type}
            onChange={selectCommand}
            required
          />
          {command ? (
            <p className="m-0 text-sm text-slate-500 dark:text-slate-400">
              {command.description}
            </p>
          ) : null}

          {command?.fields.map((field) =>
            field.kind === 'bool' ? (
              <Checkbox
                key={field.name}
                label={field.label}
                description={field.help}
                checked={values[field.name] === 'true'}
                onChange={(e) => set(field.name, e.currentTarget.checked ? 'true' : '')}
              />
            ) : field.kind === 'json' ? (
              <Textarea
                key={field.name}
                label={field.label}
                description={field.help}
                required={field.required}
                rows={4}
                classNames={{ input: 'font-mono text-xs' }}
                value={values[field.name] ?? ''}
                onChange={(e) => set(field.name, e.currentTarget.value)}
              />
            ) : field.kind === 'number' ? (
              <NumberInput
                key={field.name}
                label={field.label}
                description={field.help}
                required={field.required}
                min={0}
                allowNegative={false}
                value={values[field.name] === '' ? '' : Number(values[field.name])}
                onChange={(v) => set(field.name, v === '' ? '' : String(v))}
              />
            ) : (
              <TextInput
                key={field.name}
                label={field.label}
                description={field.help}
                required={field.required}
                classNames={{ input: 'font-mono text-xs' }}
                value={values[field.name] ?? ''}
                onChange={(e) => set(field.name, e.currentTarget.value)}
              />
            ),
          )}

          {error ? <p className="m-0 text-sm text-red-600 dark:text-red-400">{error}</p> : null}
          <Button disabled={incomplete} loading={busy} onClick={() => void handleSend()}>
            Send command
          </Button>
        </>
      )}
      {open ? <RecentActivity deviceId={deviceId} refreshKey={refreshKey} /> : null}
    </Modal>
  )
}

function RevokeModal({
  open,
  deviceId,
  serial,
  onClose,
  onRevoked,
}: {
  open: boolean
  deviceId: string
  serial: string
  onClose: () => void
  onRevoked: (device: DeviceView) => void
}) {
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleRevoke = async () => {
    setBusy(true)
    setError(null)
    try {
      const updated = await revokeDevice({
        data: { deviceId, body: { reason: reason || null } },
      })
      setReason('')
      onRevoked(updated)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not revoke the device.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Revoke device">
      <p className="m-0 text-sm text-slate-600 dark:text-slate-400">
        Revoking <span className="font-mono text-xs">{serial}</span> invalidates its credentials.
        The device stops accepting deliveries and cannot reconnect without re-provisioning.
      </p>
      <Textarea
        label="Reason (optional)"
        rows={3}
        value={reason}
        onChange={(e) => setReason(e.currentTarget.value)}
      />
      {error ? <p className="m-0 text-sm text-red-600 dark:text-red-400">{error}</p> : null}
      <div className="flex gap-2">
        <Button color="red" variant="outline" loading={busy} onClick={() => void handleRevoke()}>
          Revoke device
        </Button>
        <Button variant="default" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </Modal>
  )
}
